import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import type { Role } from '../shared/types/domain';
import { EmptyState } from '../shared/ui/Card';
import { PageSkeleton } from '../shared/ui/Status';
import { LoginPage } from '../features/auth/LoginPage';
import { PortalLayout } from './layouts/PortalLayout';
import { roleMeta, roleNavigation } from './navigation';
import { PortalPage } from './pages/PortalPage';
import { OrganizationPage } from '../features/organization/OrganizationPage';
import { PeoplePage } from '../features/organization/PeoplePage';
import { MemberDirectoryPage } from '../features/organization/MemberDirectoryPage';
import { OrderApprovalPage } from '../features/sales/OrderApprovalPage';
import { ProductManagementPage } from '../features/sales/ProductManagementPage';
import { MemberCommercePage } from '../features/sales/MemberCommercePage';
import { TeachingPage } from '../features/scheduling/TeachingPage';
import { ReversalApprovalPanel } from '../features/scheduling/ReversalApprovalPanel';
import { VenueManagementPanel } from '../features/scheduling/VenueManagementPanel';
import { RoleDashboardPage } from './pages/RoleDashboardPage';
import { MemberGrowthPage } from '../features/learning/MemberGrowthPage';
import { LowRatingPanel } from '../features/learning/LowRatingPanel';
import { EmployeeEnergyPage } from '../features/incentives/EmployeeEnergyPage';
import { EnergyApprovalPanel } from '../features/incentives/EnergyApprovalPanel';
import { IncentiveCenterPage } from '../features/incentives/IncentiveCenterPage';
import { ShopFulfillmentPanel } from '../features/shop/ShopFulfillmentPanel';
import { RefundApprovalPanel } from '../features/refunds/RefundApprovalPanel';
import { GovernanceManagementPanel } from '../features/governance/GovernanceManagementPanel';
import { RoleInboxPage } from '../features/governance/RoleInboxPage';
import { AuditExportCenterPage } from '../features/governance/AuditExportCenterPage';
import { OperationsCenterPage } from '../features/operations/OperationsCenterPage';
import { DailyWorkPanel } from '../features/dailyWork/DailyWorkPanel';

function RootRedirect() {
  const { status, session } = useAuth();
  if (status === 'loading') return <PageSkeleton />;
  if (!session) return <Navigate to="/login" replace />;
  const first = roleNavigation[session.profile.role][0];
  return <Navigate to={`${roleMeta[session.profile.role].basePath}/${first.path}`} replace />;
}

function AnonymousOnly() {
  const { status, session } = useAuth();
  if (status === 'loading') return <PageSkeleton />;
  return session ? <RootRedirect /> : <Outlet />;
}

export function RoleGuard({ role }: { role: Role }) {
  const { status, session } = useAuth();
  if (status === 'loading') return <PageSkeleton />;
  if (!session) return <Navigate to="/login" replace />;
  if (session.profile.role !== role) return <Navigate to="/forbidden" replace />;
  return <Outlet />;
}

function ForbiddenPage() {
  const { session } = useAuth();
  return <main className="standalone-state"><EmptyState title="没有访问该端口的权限" description="系统已经根据登录档案阻断跨角色访问。请返回当前账号所属端口。" action={session ? <a href={roleMeta[session.profile.role].basePath}>返回我的端口</a> : <a href="/login">返回登录</a>} /></main>;
}

function NotFoundPage() {
  return <main className="standalone-state"><EmptyState title="页面不存在" description="该地址未纳入V4页面地图。" action={<a href="/">返回首页</a>} /></main>;
}

const portalRoutes = (role: Role) => ({
  path: roleMeta[role].basePath.slice(1),
  element: <RoleGuard role={role} />,
  children: [{ element: <PortalLayout />, children: [
    { index: true, element: <Navigate to={roleNavigation[role][0].path} replace /> },
    ...roleNavigation[role].map((item) => ({ path: item.path, element: resolvePortalPage(role, item.path) })),
    ...(!roleNavigation[role].some((item) => item.path === 'profile') ? [{ path: 'profile', element: <RoleInboxPage /> }] : []),
  ] }],
});

function resolvePortalPage(role: Role, path: string) {
  const homePath = role === 'member' ? 'home' : role === 'employee' ? 'workbench' : 'dashboard';
  if (role === 'employee' && path === 'workbench') return <div className="page-stack"><DailyWorkPanel role="employee" /><RoleDashboardPage role="employee" /></div>;
  if (path === homePath) return <RoleDashboardPage role={role} />;
  if (role === 'super' && path === 'organization') return <OrganizationPage />;
  if (role === 'manager' && path === 'people') return <PeoplePage />;
  if (role === 'manager' && path === 'teaching') return <TeachingPage />;
  if (role === 'manager' && path === 'tasks') return <><DailyWorkPanel role="manager" /><OrderApprovalPage /><ReversalApprovalPanel /><LowRatingPanel /><EnergyApprovalPanel /><ShopFulfillmentPanel /><RefundApprovalPanel /></>;
  if (role === 'manager' && path === 'management') return <><OperationsCenterPage role="manager" /><ProductManagementPage /><VenueManagementPanel /><GovernanceManagementPanel /></>;
  if (role === 'manager' && path === 'members') return <MemberDirectoryPage managerView />;
  if (role === 'employee' && path === 'members') return <><OperationsCenterPage role="employee" sections={['crm', 'member-risk', 'marketing']} compact /><MemberDirectoryPage /></>;
  if (role === 'employee' && path === 'teaching') return <TeachingPage />;
  if (role === 'employee' && path === 'star-energy') return <><OperationsCenterPage role="employee" sections={['workforce', 'assistant']} compact /><EmployeeEnergyPage /></>;
  if (path === 'profile') return <RoleInboxPage />;
  if (role === 'super' && path === 'business') return <><DailyWorkPanel role="super" /><OperationsCenterPage role="super" /><ProductManagementPage superView /><OrderApprovalPage /><ReversalApprovalPanel /><LowRatingPanel /><RefundApprovalPanel /></>;
  if (role === 'super' && path === 'incentives') return <><IncentiveCenterPage /><EnergyApprovalPanel /><ShopFulfillmentPanel /></>;
  if (role === 'super' && path === 'system') return <AuditExportCenterPage />;
  if (role === 'member' && path === 'courses') return <MemberCommercePage view="packages" />;
  if (role === 'member' && path === 'growth') return <MemberGrowthPage />;
  if (role === 'member' && path === 'spending') return <MemberCommercePage view="orders" />;
  return <PortalPage role={role} page={path} />;
}

export const router = createBrowserRouter([
  { path: '/', element: <RootRedirect /> },
  { element: <AnonymousOnly />, children: [{ path: '/login', element: <LoginPage /> }] },
  portalRoutes('member'), portalRoutes('employee'), portalRoutes('manager'), portalRoutes('super'),
  { path: '/forbidden', element: <ForbiddenPage /> },
  { path: '*', element: <NotFoundPage /> },
]);
